import {NotificationsSubscriptionsMenu} from '@github-ui/notifications-subscriptions-menu'
import {PinButton, shouldShowPinButton} from '@github-ui/repo-pin-button'
import type {PinButtonProps} from '@github-ui/repo-pin-button/types'
import {StarButton} from '@github-ui/repo-star-button'
import {TemplateButton} from '@github-ui/repo-template-button'
import type {HeaderResponse, RepoPaths} from '../types'
import {ForkButton} from '@github-ui/repo-fork-button'
import {useErrorMessage} from '../utils/use-error-message'
import {useSearchParams} from '@github-ui/use-navigate'
import {HeaderSponsorButton} from './HeaderSponsorButton'

export type CodeViewHeaderActionsProps = Pick<
  HeaderResponse,
  | 'repo'
  | 'forksCount'
  | 'stargazerCount'
  | 'showSponsorButton'
  | 'showTemplateButton'
  | 'star'
  | 'fork'
  | 'pin'
  | 'watch'
  | 'viewer'
  | 'ownerLogin'
  | 'repoName'
> & {
  paths: RepoPaths
  hasAccessToCodespaces?: boolean
}

/** Desktop action button container with Watch, Fork, and Star buttons. */
export function CodeViewHeaderActions({
  repo,
  pin,
  watch,
  viewer,
  paths,
  ownerLogin,
  repoName,
  star,
  stargazerCount,
  fork,
  forksCount,
  showSponsorButton,
  showTemplateButton,
  hasAccessToCodespaces,
}: CodeViewHeaderActionsProps) {
  const pinProps: PinButtonProps = {
    repoNameWithOwner: paths.repoNameWithOwner,
    repoUrl: paths.repoUrl,
    isPrivate: repo.isPrivate,
    isOrgOwned: pin.isOrgOwned,
    canPin: pin.canPin,
    isPinned: pin.isPinned,
    isLoggedIn: viewer.isLoggedIn,
    pinItemsRemaining: pin.pinItemsRemaining,
    orgPinData: pin.orgPinData,
  }

  const showBanner = useErrorMessage()
  const [searchParams] = useSearchParams()
  const sponsorAutoOpen = searchParams.get('sponsor') === '1'

  return (
    <ul
      className="list-style-none d-flex flex-shrink-0 flex-wrap flex-items-center flex-justify-end gap-2 py-1"
      data-testid="repo-header-actions"
    >
      {showSponsorButton && (
        <li>
          <HeaderSponsorButton
            ownerLogin={ownerLogin}
            repoName={repoName}
            repoNameWithOwner={paths.repoNameWithOwner}
            isOrgOwned={pin.isOrgOwned}
            defaultOpen={sponsorAutoOpen}
          />
        </li>
      )}

      {shouldShowPinButton(pinProps) && (
        <li>
          <PinButton
            {...pinProps}
            onError={error => {
              const message = error.message || 'Unable to pin repository. Please try again.'
              showBanner(message)
            }}
          />
        </li>
      )}

      {viewer.isLoggedIn && watch.canWatch && (
        <li>
          <NotificationsSubscriptionsMenu
            repositoryId={watch.watchData.repositoryId}
            repositoryName={watch.watchData.repositoryName}
            watchersCount={watch.watchData.watchersCount}
            subscriptionType={watch.watchData.subscriptionType}
            subscribableThreadTypes={watch.watchData.subscribableThreadTypes}
            showLabelSubscriptions={watch.watchData.showLabelSubscriptions}
            subscribedLabels={watch.watchData.subscribedLabels}
          />
        </li>
      )}

      <li>
        <ForkButton
          forkCount={forksCount}
          canFork={fork.canFork}
          forkabilityError={fork.forkabilityError}
          repoNameWithOwner={paths.repoNameWithOwner}
          forkSelectPath={paths.forkSelectPath}
          forksApiPath={paths.forksApiPath}
          isLoggedIn={viewer.isLoggedIn}
        />
      </li>

      <li>
        <StarButton
          starred={star.viewerHasStarred}
          starCount={stargazerCount}
          starPath={paths.starPath}
          unstarPath={paths.unstarPath}
          canStar={star.canStar}
          isLoggedIn={viewer.isLoggedIn}
          listsApiPath={paths.listsApiPath}
          listsUpdatePath={paths.listsUpdatePath}
          repoNameWithOwner={paths.repoNameWithOwner}
          emuContributionBlocked={viewer.emuContributionBlocked}
        />
      </li>

      {showTemplateButton && repo.isTemplate && (
        <li>
          <TemplateButton
            templatePath={paths.templatePath}
            showTemplateButton={showTemplateButton}
            hasCodespacesAccess={hasAccessToCodespaces ?? false}
            onError={error => {
              const message = error.message || 'Unable to create template. Please try again.'
              showBanner(message)
            }}
          />
        </li>
      )}
    </ul>
  )
}
